import { supabase } from '../lib/supabaseClient.js';
import { TABLES, TICKET_STATUS } from '../lib/constants.js';

// ملاحظة معمارية مهمة: حسب وصفك، قاعدة بياناتك بها بالفعل
// - trigger لتوليد ticket_no / report_no تلقائيًا (لا نولّدهما هنا إطلاقاً)
// - trigger لتسجيل ticket_timeline تلقائيًا عند تغيّر status (لا ندرج يدويًا عند تغيير الحالة)
// - trigger للتحقق من صحة انتقال الحالة (workflow validation) — أي انتقال غير مسموح
//   سيرفضه Postgres نفسه برسالة خطأ، ما يحقق "لا يمكن لأي مستخدم تجاوز صلاحياته"
//   فعليًا على مستوى قاعدة البيانات وليس فقط بإخفاء الأزرار.
// لذلك الدوال هنا تبقى بسيطة: تحديث الحقل المطلوب فقط وترك البقية لـ Postgres.

export async function createTicket({ unitId, propertyId, tenantId, title, problemTypeId, priority, description }) {
  const { data, error } = await supabase.from(TABLES.TICKETS).insert({
    unit_id: unitId, property_id: propertyId, tenant_id: tenantId || null,
    title, problem_type_id: problemTypeId || null, priority,
    status: TICKET_STATUS.NEW, description
  }).select().single();
  if (error) throw error; // إن رفضت RLS الإدراج (مثلاً شقة ليست شقتك) سيظهر الخطأ هنا
  return data;
}

/** يعتمد على RLS لديك لعرض فقط ما يحق لهذا المستخدم رؤيته —
 *  لا حاجة لفلترة يدوية بالدور في الواجهة كما كان في نسخة Claude. */
export async function listMyTickets() {
  const { data, error } = await supabase.from(TABLES.TICKETS).select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function getTicket(id) {
  const { data, error } = await supabase.from(TABLES.TICKETS).select('*').eq('id', id).single();
  if (error) throw error;
  return data;
}

export async function startReview(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS).update({ status: TICKET_STATUS.UNDER_REVIEW }).eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function assignForeman(ticketId, foremanId) {
  const { data, error } = await supabase.from(TABLES.TICKETS)
    .update({ foreman_id: foremanId, status: TICKET_STATUS.ASSIGNED_TO_FOREMAN })
    .eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function assignTechnician(ticketId, technicianId, priority) {
  const patch = { technician_id: technicianId, status: TICKET_STATUS.ASSIGNED_TO_TECHNICIAN };
  if (priority) patch.priority = priority;
  const { data, error } = await supabase.from(TABLES.TICKETS).update(patch).eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function techReceive(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS)
    .update({ status: TICKET_STATUS.TECHNICIAN_RECEIVED })
    .eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function techInProgress(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS).update({ status: TICKET_STATUS.IN_PROGRESS }).eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function techSolved(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS)
    .update({ status: TICKET_STATUS.TECHNICIAN_SOLVED })
    .eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function techSubmitForApproval(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS)
    .update({ status: TICKET_STATUS.WAITING_FOREMAN_APPROVAL })
    .eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function sendBackToTech(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS).update({ status: TICKET_STATUS.REOPENED }).eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

/** Approve first; the database trigger records approval time and assigns the report number. */
export async function approveTicket(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS)
    .update({ status: TICKET_STATUS.APPROVED })
    .eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function closeTicket(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS)
    .update({ status: TICKET_STATUS.CLOSED })
    .eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function cancelTicket(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS).update({ status: TICKET_STATUS.CANCELLED }).eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

export async function reopenTicket(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKETS).update({ status: TICKET_STATUS.REOPENED }).eq('id', ticketId).select().single();
  if (error) throw error;
  return data;
}

// -------------------- Timeline --------------------
export async function listTimeline(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKET_TIMELINE).select('*').eq('ticket_id', ticketId).order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

// -------------------- Costs --------------------
/** تظهر فقط لمن تسمح له RLS بقراءة ticket_costs (المدير والفورمان حسب طلبك) */
export async function listCosts(ticketId) {
  const { data, error } = await supabase.from(TABLES.TICKET_COSTS).select('*').eq('ticket_id', ticketId).order('created_at');
  if (error) throw error;
  return data;
}

export async function addCost(ticketId, { itemName, quantity, unitPrice, notes = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase.from(TABLES.TICKET_COSTS).insert({
    ticket_id: ticketId, item_name: itemName, quantity, unit_price: unitPrice,
    notes, created_by: user?.id || null
  }).select().single();
  if (error) throw error;
  return data;
}

export async function removeCost(costId) {
  const { error } = await supabase.from(TABLES.TICKET_COSTS).delete().eq('id', costId);
  if (error) throw error;
}

// -------------------- الاشتراك اللحظي (Realtime) --------------------
export function subscribeTickets(onChange) {
  return supabase.channel('tickets-changes')
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.TICKETS }, onChange)
    .subscribe();
}
export function subscribeTimeline(ticketId, onChange) {
  return supabase.channel('timeline-' + ticketId)
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: TABLES.TICKET_TIMELINE, filter: `ticket_id=eq.${ticketId}` }, onChange)
    .subscribe();
}
